import React, { useMemo, useState } from 'react';
import type { EventTimeline, TCEventTimeline } from './core/Timelines';
import { isTCTimeline } from './core/Timelines';
import Modal, { ModalBody, ModalFooter } from './ui/Modal';
import {
  Box,
  Button,
  DatePicker,
  FormField,
  FormRow,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
  TimeField,
} from './ui';
import { useInactivateVesselsMutation } from '../graphql';
import { compact } from 'lodash';
import { gql } from '@apollo/client';
import LoadingSpinner from './LoadingSpinner';

type FleetCleanupModalProps = {
  onClose: () => void;
  timelines: EventTimeline[];
};

function getDefaultCutoffDate() {
  const date = new Date();
  date.setDate(date.getDate() - 14);

  return date;
}

export function useFleetCleanup(timelines: EventTimeline[]) {
  const [cutoffDate, setCutoffDate] = useState(getDefaultCutoffDate());
  const [selectedVesselIds, setSelectedVesselIds] = useState<number[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [inactivateVessels, { error, loading: isLoading }] =
    useInactivateVesselsMutation({
      update: (cache, { data }) => {
        const vessels = compact(data?.inactivateShips?.ships);

        for (const vessel of vessels) {
          cache.writeFragment({
            id: cache.identify({
              __typename: 'Ship',
              id: vessel.id,
            }),
            fragment: gql`
              fragment InactivatedVessel on Ship {
                id
                inactive
              }
            `,
            data: vessel,
          });
        }
      },
    });

  const vesselsWithLastActivity = useMemo(() => {
    return timelines
      .filter(isTCTimeline)
      .filter((tl) => !tl.vessel.inactive)
      .map(getTimelineLastActivity);
  }, [timelines]);

  const vesselsToInactivate = vesselsWithLastActivity.filter(
    ({ lastActivityAt }) =>
      lastActivityAt === null || lastActivityAt < cutoffDate.getTime(),
  );

  const allVesselIds = vesselsToInactivate.map(({ vesselId }) => vesselId);
  const allVesselsSelected =
    allVesselIds.length > 0 &&
    allVesselIds.every((vesselId) => selectedVesselIds.includes(vesselId));

  function toggleVessel(vesselId: number) {
    setSelectedVesselIds((curSelectedVesselIds) => {
      const isSelected = curSelectedVesselIds.includes(vesselId);

      return isSelected
        ? curSelectedVesselIds.filter((id) => id !== vesselId)
        : [...curSelectedVesselIds, vesselId];
    });
  }

  function toggleAllVessels() {
    setSelectedVesselIds(() => {
      return allVesselsSelected ? [] : allVesselIds;
    });
  }

  async function inactivateSelectedVessels() {
    try {
      setSuccessMessage(null);

      await inactivateVessels({
        variables: {
          vesselIds: selectedVesselIds,
        },
      });

      setSuccessMessage(`Inactivated ${selectedVesselIds.length} vessels`);
      setSelectedVesselIds([]);
    } catch (err) {
      console.error('Fleet cleanup mutation error:', err);
    }
  }

  return {
    allVesselsSelected,
    cutoffDate,
    errorMessage: error?.message ?? null,
    inactivateSelectedVessels,
    isLoading,
    selectedVesselIds,
    setCutoffDate,
    successMessage,
    toggleAllVessels,
    toggleVessel,
    vesselsToInactivate,
  };
}

function getTimelineLastActivity(timeline: TCEventTimeline) {
  let lastActivityAt: number | null = null;

  for (const voyageGroup of timeline.voyageGroups) {
    const lastEventEndTime = voyageGroup.events.at(-1)?.end;
    if (!lastEventEndTime) continue;

    if (!lastActivityAt || lastActivityAt < lastEventEndTime) {
      lastActivityAt = lastEventEndTime;
    }
  }

  return {
    vesselId: timeline.vesselId,
    vesselName: timeline.vessel.name ?? `Vessel #${timeline.vesselId}`,
    lastActivityAt,
  };
}

export function FleetCleanupModal(props: FleetCleanupModalProps) {
  const {
    allVesselsSelected,
    cutoffDate,
    errorMessage,
    inactivateSelectedVessels,
    isLoading,
    selectedVesselIds,
    setCutoffDate,
    successMessage,
    toggleAllVessels,
    toggleVessel,
    vesselsToInactivate,
  } = useFleetCleanup(props.timelines);

  return (
    <Modal isOpen={true} onClose={props.onClose} width="800px" height="800px">
      <ModalBody>
        <Text fontSize={3} fontWeight="bold" mb={3}>
          Fleet cleanup
        </Text>
        <Text mb={4}>
          This will set all vessels with no activity after the selected date as
          inactive. Inactivated vessels can be reactivated via the vessel admin
          panel.
        </Text>
        {errorMessage && (
          <Text fontWeight="bold" color="#FC4A15" mb={4}>
            Error inactivating vessels: {errorMessage}
          </Text>
        )}
        <FormRow zIndex="2">
          <FormField label="Last activity before" width={200}>
            <DatePicker
              selected={cutoffDate}
              onChange={(newDate) =>
                newDate ? setCutoffDate(newDate) : void null
              }
              timeZone="UTC"
              showTime={false}
            />
          </FormField>
        </FormRow>
        <Box
          borderColor="neutral.gray_225"
          borderWidth={1}
          borderStyle="solid"
          overflow="scroll"
        >
          <Table>
            <thead>
              <TableHeaders isSticky>
                <TableHeader width={50}>
                  <input
                    type="checkbox"
                    checked={allVesselsSelected}
                    onChange={toggleAllVessels}
                    disabled={isLoading}
                  />
                </TableHeader>
                <TableHeader>Vessel</TableHeader>
                <TableHeader>Last activity</TableHeader>
              </TableHeaders>
            </thead>
            <tbody>
              {vesselsToInactivate.map((vessel) => (
                <TableRow key={vessel.vesselId}>
                  <TableCell>
                    <input
                      id={`inactivate-${vessel.vesselId}`}
                      type="checkbox"
                      checked={selectedVesselIds.includes(vessel.vesselId)}
                      onChange={() => toggleVessel(vessel.vesselId)}
                      disabled={isLoading}
                    />
                  </TableCell>
                  <TableCell>
                    <label htmlFor={`inactivate-${vessel.vesselId}`}>
                      {vessel.vesselName}
                    </label>
                  </TableCell>
                  <TableCell>
                    {vessel.lastActivityAt ? (
                      <TimeField value={vessel.lastActivityAt} />
                    ) : (
                      <Text>No activity</Text>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Text color="neutral.white_50" mr={3}>
          {selectedVesselIds.length} vessels selected
        </Text>
        {isLoading && (
          <Box mr={3}>
            <LoadingSpinner />
          </Box>
        )}
        {successMessage && (
          <Text fontWeight="bold" color="#5fad09" mr={3}>
            ✓ {successMessage}
          </Text>
        )}
        {errorMessage && (
          <Text fontWeight="bold" color="#FC4A15" mr={3}>
            ✗ Error
          </Text>
        )}
        <Button
          disabled={selectedVesselIds.length < 1 || isLoading}
          onPress={inactivateSelectedVessels}
        >
          Inactivate vessels
        </Button>
      </ModalFooter>
    </Modal>
  );
}
